
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "@/lib/auth/auth-context";
import { SignInPage } from "@/components/ui/sign-in";
import { cx } from "@/lib/utils/cx";

const DEMO_ACCOUNTS = [
  { email: "director@dealership.com",  password: "director123",  role: "Director"      },
  { email: "gm@dealership.com",        password: "gm123",        role: "Gen. Manager"  },
  { email: "service@dealership.com",   password: "service123",   role: "Svc. Manager"  },
  { email: "advisor@dealership.com",   password: "advisor123",   role: "Svc. Advisor"  },
  { email: "manager@bodyshop.com",     password: "manager123",   role: "Shop Manager"  },
  { email: "estimator@bodyshop.com",   password: "estimator123", role: "Estimator"     },
  { email: "salesmgr@dealership.com",  password: "salesmgr123",  role: "Sales Mgr."    },
  { email: "salesrep@dealership.com",  password: "salesrep123",  role: "Sales Rep"     },
  { email: "multirole@lafontaine.com", password: "multirole123", role: "Multi-Role"    },
];

export default function LoginPage() {
  const location = useLocation();
  // After accepting an invite for an existing account, the invite page navigates here
  // with state.prefillEmail so the user doesn't have to remember which email it was for.
  const prefillEmail = (location.state as { prefillEmail?: string } | null)?.prefillEmail ?? "";
  const [email, setEmail]       = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [error, setError]       = useState("");

  const { user, isLoading: isAuthLoading, signIn } = useAuth();
  const navigate     = useNavigate();

  useEffect(() => {
    if (!isAuthLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthLoading, user, navigate]);

  if (isAuthLoading || user) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password) return;
    setError("");
    setLoading(true);
    try {
      const { hasTeams } = await signIn(email, password);
      if (!hasTeams) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (acc: (typeof DEMO_ACCOUNTS)[0]) => {
    setEmail(acc.email);
    setPassword(acc.password);
    setError("");
  };

  return (
    <SignInPage
      title={
        <span className="font-bold tracking-tight" style={{ fontFamily: "var(--font-syne)" }}>
          eT<span className="text-primary-500">X</span>
          <span className="text-zinc-300 mx-0.5 text-3xl font-light">·</span>
          Impa<span className="text-primary-500">X</span>t
        </span>
      }
      description="AI-powered collision damage estimation. Sign in to your workspace."
      heroVariant="signin"
      email={email}
      password={password}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onSignIn={handleSubmit}
      onResetPassword={() => navigate("/forgot-password")}
      onCreateAccount={() => navigate("/signup")}
      isLoading={isLoading}
      error={error}
    >
      {/* Demo accounts — hidden unless VITE_SHOW_DEMO_ACCOUNTS=true (off by default) */}
      {import.meta.env.VITE_SHOW_DEMO_ACCOUNTS === "true" && (
        <div className="pt-2">
          <p className="text-xs text-zinc-400 uppercase tracking-widest mb-3">
            Demo access
          </p>
          <div className="flex flex-wrap gap-1.5">
            {DEMO_ACCOUNTS.map((acc) => (
              <button
                key={acc.email}
                type="button"
                onClick={() => fillDemo(acc)}
                className={cx(
                  "h-7 px-2.5 rounded-lg text-[11px] font-medium transition-all duration-100 border",
                  email === acc.email
                    ? "border-zinc-900 text-zinc-900 bg-zinc-100"
                    : "border-zinc-200 text-zinc-400 hover:border-zinc-400 hover:text-zinc-700 hover:bg-zinc-50"
                )}
              >
                {acc.role}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-zinc-300 pt-2">
        © {new Date().getFullYear()} Badders Holding Company LLC
      </p>
    </SignInPage>
  );
}
